/**
 * academyTrainings.ts - Academy training definitions (7 types)
 *
 * These are the advanced trainings available in the Academy view.
 * Each ghost can train each type up to level 5.
 */

export type AcademyTrainingId =
  | 'acting'
  | 'human_relations'
  | 'psychology'
  | 'special_fx'
  | 'fitness'
  | 'teamwork'
  | 'customer_service';

export interface AcademyTrainingDef {
  id: AcademyTrainingId;
  nameKey: string;
  descKey: string;
  effectKey: string;
  baseDurationMs: number;       // duration for level 1 training
  baseCostSp: number;           // SP cost for level 1
  maxLevel: number;             // always 5
  costMultiplierPerLevel: number;
  durationMultiplierPerLevel: number;
}

export const ACADEMY_TRAINING_DEFS: Record<AcademyTrainingId, AcademyTrainingDef> = {
  acting: {
    id: 'acting',
    nameKey: 'academy_acting',
    descKey: 'academy_acting_desc',
    effectKey: 'academy_acting_effect',
    baseDurationMs: 30000,      // 30s
    baseCostSp: 100,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  human_relations: {
    id: 'human_relations',
    nameKey: 'academy_human_relations',
    descKey: 'academy_human_relations_desc',
    effectKey: 'academy_human_relations_effect',
    baseDurationMs: 45000,      // 45s
    baseCostSp: 120,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  psychology: {
    id: 'psychology',
    nameKey: 'academy_psychology',
    descKey: 'academy_psychology_desc',
    effectKey: 'academy_psychology_effect',
    baseDurationMs: 60000,      // 60s
    baseCostSp: 150,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  special_fx: {
    id: 'special_fx',
    nameKey: 'academy_special_fx',
    descKey: 'academy_special_fx_desc',
    effectKey: 'academy_special_fx_effect',
    baseDurationMs: 60000,      // 60s
    baseCostSp: 180,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  fitness: {
    id: 'fitness',
    nameKey: 'academy_fitness',
    descKey: 'academy_fitness_desc',
    effectKey: 'academy_fitness_effect',
    baseDurationMs: 40000,      // 40s
    baseCostSp: 80,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  teamwork: {
    id: 'teamwork',
    nameKey: 'academy_teamwork',
    descKey: 'academy_teamwork_desc',
    effectKey: 'academy_teamwork_effect',
    baseDurationMs: 90000,      // 90s
    baseCostSp: 200,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
  customer_service: {
    id: 'customer_service',
    nameKey: 'academy_customer_service',
    descKey: 'academy_customer_service_desc',
    effectKey: 'academy_customer_service_effect',
    baseDurationMs: 50000,      // 50s
    baseCostSp: 100,
    maxLevel: 5,
    costMultiplierPerLevel: 2.0,
    durationMultiplierPerLevel: 1.5,
  },
};

export const ALL_ACADEMY_TRAINING_IDS = Object.keys(ACADEMY_TRAINING_DEFS) as AcademyTrainingId[];

/** Calculate cost for a specific level */
export function academyTrainingCost(trainingId: AcademyTrainingId, currentLevel: number): number {
  const def = ACADEMY_TRAINING_DEFS[trainingId];
  if (!def) return 0;
  return Math.floor(def.baseCostSp * Math.pow(def.costMultiplierPerLevel, currentLevel));
}

/** Calculate duration for a specific level */
export function academyTrainingDuration(trainingId: AcademyTrainingId, currentLevel: number): number {
  const def = ACADEMY_TRAINING_DEFS[trainingId];
  if (!def) return 0;
  return Math.floor(def.baseDurationMs * Math.pow(def.durationMultiplierPerLevel, currentLevel));
}
